/**
 * Trials admin page.
 *
 * Layout:
 *   PageHeader → HeroPanel (signups / converted / churned + rate)
 *              → 30-day conversion timeline (paid_conversions vs trials_created)
 *              → Auto-convert rules card (policy + hooks for future config)
 *              → Per-trial card grid: progress bar, days-left chip, convert CTA
 *
 * "Converted" + "Churned" come from analytics + license windows since
 * there's no direct trial→paid linkage in the API:
 *   converted = sum(analyticsTimeseries paid_conversions, last 30d)
 *   churned   = trials whose expires_at < now and are no longer active
 *   live      = trials that are status=active AND expires_at > now
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Hourglass, RefreshCw, TrendingUp, Sparkles, ArrowRight,
  CheckCircle2, AlertOctagon, Settings,
} from 'lucide-react';
import {
  AdminApiClient,
  type LicenseRow, type TimeseriesResponse,
} from '../api';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, bandFromStatus, type Band } from '../components/ui/StatusChip';
import { FetchState } from '../components/ui/FetchState';
import { failureFrom, type Failure } from '../lib/api-state';

type RangePreset = 'all' | '7d' | '3d' | 'expired';

const TRIAL_DURATION_DAYS = 14;

export function TrialsPage(): JSX.Element {
  const { state } = useSession();
  const nav = useNavigate();
  const [range, setRange] = useState<RangePreset>('all');
  const [trials, setTrials] = useState<LicenseRow[] | null>(null);
  const [trialSeries, setTrialSeries] = useState<TimeseriesResponse | null>(null);
  const [convSeries, setConvSeries] = useState<TimeseriesResponse | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );

  useEffect(() => {
    setRefreshing(true);
    const now = Date.now();
    const expires_before = (() => {
      switch (range) {
        case '7d':      return new Date(now + 7 * 86_400_000).toISOString();
        case '3d':      return new Date(now + 3 * 86_400_000).toISOString();
        case 'expired': return new Date(now).toISOString();
        default:        return undefined;
      }
    })();
    const since = new Date(now - 30 * 86_400_000).toISOString();
    Promise.all([
      api.licenses({ license_type: 'trial', limit: 500, expires_before })
        .then((r) => r.licenses)
        .catch((e) => { setErr(failureFrom(e, 'the trial list')); return null; }),
      // The two series only feed the signup / conversion figures. If one
      // fails those figures show "—" rather than a made-up 0.
      api.analyticsTimeseries({ metric: 'trials_created', interval: 'day', since })
        .catch((e) => { failureFrom(e, 'trial signups'); return null; }),
      api.analyticsTimeseries({ metric: 'paid_conversions', interval: 'day', since })
        .catch((e) => { failureFrom(e, 'paid conversions'); return null; }),
    ]).then(([t, ts, cs]) => {
      if (t) setErr(null);
      setTrials(t);
      setTrialSeries(ts);
      setConvSeries(cs);
    }).finally(() => setRefreshing(false));
  }, [api, range, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const stats = useMemo(() => {
    const now = Date.now();
    let live = 0, churned = 0;
    for (const r of trials ?? []) {
      const expT = r.expires_at ? Date.parse(r.expires_at) : null;
      const isLive = r.status === 'active' && (expT == null || expT > now);
      if (isLive) live += 1;
      else churned += 1;
    }
    const trialSignups = trialSeries ? trialSeries.points.reduce((s, p) => s + p.value, 0) : null;
    const conversions = convSeries ? convSeries.points.reduce((s, p) => s + p.value, 0) : null;
    const conversionRate = trialSignups !== null && conversions !== null && trialSignups > 0
      ? Math.round((conversions / trialSignups) * 100)
      : null;
    return { live, churned, signups30: trialSignups, conversions, conversionRate };
  }, [trials, trialSeries, convSeries]);

  return (
    <div className="page">
      <PageHeader
        title="Trials"
        subtitle="Every trial license. Filter to who's about to churn unless they upgrade."
        icon={Hourglass}
        accent="#fb923c"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      {trials && (
      <HeroPanel
        icon={Sparkles}
        accent="#fb923c"
        title="Trial pipeline"
        subtitle="30-day signup vs conversion velocity, plus what's currently live."
        kpis={[
          <HeroKpi key="live" label="Live" value={stats.live}
                   band={stats.live > 0 ? 'healthy' : 'neutral'}
                   hint="not yet expired" />,
          <HeroKpi key="su" label="Signups (30d)" value={stats.signups30 ?? '—'}
                   band={stats.signups30 !== null && stats.signups30 > 0 ? 'healthy' : 'neutral'} />,
          <HeroKpi key="cv" label="Converted (30d)" value={stats.conversions ?? '—'}
                   band={stats.conversions !== null && stats.conversions > 0 ? 'healthy' : 'neutral'} />,
          <HeroKpi key="ch" label="Churned" value={stats.churned}
                   band={stats.churned > 0 ? 'critical' : 'neutral'}
                   hint="expired without conversion" />,
          <HeroKpi key="rt" label="Conversion rate" value={stats.conversionRate === null ? '—' : `${stats.conversionRate}%`}
                   band={stats.conversionRate === null ? 'neutral'
                        : stats.conversionRate >= 30 ? 'healthy'
                        : stats.conversionRate >= 10 ? 'degraded'
                        : 'warning'}
                   hint={stats.conversionRate === null ? 'not enough data' : undefined} />,
        ]}
      />
      )}

      <div style={{ marginTop: 16 }}>
        {trialSeries || convSeries || !trials
          ? <ConversionTimeline trials={trialSeries} conversions={convSeries} />
          : <p role="alert" style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              Signup and conversion history is unavailable right now.
            </p>}
      </div>

      <div style={{ marginTop: 16 }}>
        <AutoConvertRules />
      </div>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <RangeSelector value={range} onChange={setRange} />

        <FetchState
          loading={!trials && !err}
          error={err}
          empty={!!trials && trials.length === 0}
          emptyTitle="No trials match"
          emptyDescription="Try a different window — trials show up here as they're issued."
          onRetry={onRefresh}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 12,
            }}
          >
            {(trials ?? []).map((r) => (
              <TrialCard
                key={r.id}
                row={r}
                onConvert={() => nav(`/licenses?focus=${encodeURIComponent(r.id)}`)}
              />
            ))}
          </div>
        </FetchState>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * 30-day conversion timeline
 * ────────────────────────────────────────────────────────────────── */

function ConversionTimeline({
  trials, conversions,
}: {
  trials: TimeseriesResponse | null;
  conversions: TimeseriesResponse | null;
}): JSX.Element {
  const days = useMemo(() => {
    const now = new Date();
    const out: Array<{ key: string; label: string; trials: number; conversions: number }> = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      out.push({
        key,
        label: d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }),
        trials: 0,
        conversions: 0,
      });
    }
    const idx = new Map(out.map((d, i) => [d.key, i] as const));
    for (const p of trials?.points ?? []) {
      const k = p.bucket.slice(0, 10);
      const i = idx.get(k);
      if (i !== undefined && out[i]) out[i].trials += p.value;
    }
    for (const p of conversions?.points ?? []) {
      const k = p.bucket.slice(0, 10);
      const i = idx.get(k);
      if (i !== undefined && out[i]) out[i].conversions += p.value;
    }
    return out;
  }, [trials, conversions]);

  const max = Math.max(1, ...days.map((d) => Math.max(d.trials, d.conversions)));

  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <TrendingUp width={14} height={14} color="var(--text-muted)" />
        <span
          style={{
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Trial signups vs paid conversions · last 30 days
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, fontSize: '0.6875rem' }}>
          <Legend tone="#fb923c" label="Signups" />
          <Legend tone="#34d399" label="Conversions" />
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
          gap: 4,
          alignItems: 'end',
          height: 100,
        }}
      >
        {days.map((d) => (
          <DayColumn
            key={d.key}
            label={d.label}
            signups={d.trials}
            conversions={d.conversions}
            max={max}
          />
        ))}
      </div>
    </section>
  );
}

function Legend({ tone, label }: { tone: string; label: string }): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-muted)' }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: tone }} />
      {label}
    </span>
  );
}

function DayColumn({
  label, signups, conversions, max,
}: {
  label: string; signups: number; conversions: number; max: number;
}): JSX.Element {
  const sH = (signups / max) * 100;
  const cH = (conversions / max) * 100;
  return (
    <div
      title={`${label} · signups ${signups} · conversions ${conversions}`}
      style={{
        display: 'flex',
        gap: 1,
        height: '100%',
        alignItems: 'flex-end',
      }}
    >
      <div
        style={{
          flex: 1,
          height: `${Math.max(2, sH)}%`,
          background: signups > 0 ? '#fb923c' : 'rgba(148,163,184,0.10)',
          borderRadius: 2,
        }}
      />
      <div
        style={{
          flex: 1,
          height: `${Math.max(2, cH)}%`,
          background: conversions > 0 ? '#34d399' : 'rgba(148,163,184,0.10)',
          borderRadius: 2,
        }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Auto-convert rules card
 * ────────────────────────────────────────────────────────────────── */

function AutoConvertRules(): JSX.Element {
  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Settings width={14} height={14} color="var(--text-muted)" />
        <h3
          style={{
            margin: 0,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Auto-convert policy
        </h3>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '0.6875rem',
            color: 'var(--text-muted)',
          }}
        >
          Editable from API once /internal/trial-rules lands
        </span>
      </div>

      <ul
        style={{
          listStyle: 'none', padding: 0, margin: 0,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        <RuleRow tone="#34d399" label="Day 0–7" hint="Trial provisioned, no nag screens. Customers self-serve via the dashboard." />
        <RuleRow tone="#fb923c" label="Day 8–13" hint="In-app banner + email reminders to enter billing details. Activation cap stays at 3 servers." />
        <RuleRow tone="#ef4444" label="Day 14" hint="Trial expires. License flips to status=expired. Heartbeats start refusing new activations within 6h." />
        <RuleRow tone="#22d3ee" label="Convert anytime" hint="“Convert to paid” flows route through Send checkout link → Stripe → license auto-promotes on webhook." />
      </ul>
    </section>
  );
}

function RuleRow({
  tone, label, hint,
}: { tone: string; label: string; hint: string }): JSX.Element {
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '8px 12px',
        borderRadius: 10,
        background: 'var(--bg-overlay)',
        border: '1px solid var(--border)',
      }}
    >
      <span
        aria-hidden
        style={{
          flex: 'none',
          width: 4,
          alignSelf: 'stretch',
          background: tone,
          borderRadius: 2,
          minHeight: 24,
        }}
      />
      <div>
        <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-primary)' }}>
          {label}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {hint}
        </div>
      </div>
    </li>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Range selector
 * ────────────────────────────────────────────────────────────────── */

function RangeSelector({
  value, onChange,
}: { value: RangePreset; onChange: (v: RangePreset) => void }): JSX.Element {
  const options: Array<{ key: RangePreset; label: string }> = [
    { key: 'all',     label: 'All trials' },
    { key: '7d',      label: 'Expires ≤ 7d' },
    { key: '3d',      label: 'Expires ≤ 3d' },
    { key: 'expired', label: 'Already expired' },
  ];
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
        width: 'fit-content', flexWrap: 'wrap',
      }}
    >
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            aria-pressed={active}
            onClick={() => onChange(o.key)}
            style={{
              padding: '6px 12px',
              border: 'none',
              borderRadius: 8,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Per-trial card with progress bar
 * ────────────────────────────────────────────────────────────────── */

function TrialCard({
  row, onConvert,
}: { row: LicenseRow; onConvert: () => void }): JSX.Element {
  const issuedT = Date.parse(row.issued_at);
  const expiresT = row.expires_at ? Date.parse(row.expires_at) : null;
  const now = Date.now();

  const totalMs = expiresT != null && Number.isFinite(issuedT)
    ? Math.max(1, expiresT - issuedT)
    : TRIAL_DURATION_DAYS * 86_400_000;
  const elapsedMs = Math.max(0, now - issuedT);
  const pct = Math.max(0, Math.min(100, (elapsedMs / totalMs) * 100));
  const daysLeft = expiresT != null
    ? Math.ceil((expiresT - now) / 86_400_000)
    : null;
  const expired = daysLeft != null && daysLeft <= 0;

  let urgencyTone = '#34d399';
  let urgencyLabel = `${daysLeft}d left`;
  let urgencyBand: Band = 'healthy';
  if (expired) {
    urgencyTone = '#f87171';
    urgencyLabel = 'expired';
    urgencyBand = 'critical';
  } else if (daysLeft != null && daysLeft <= 3) {
    urgencyTone = '#fbbf24';
    urgencyLabel = `${daysLeft}d left`;
    urgencyBand = 'warning';
  } else if (daysLeft != null && daysLeft <= 7) {
    urgencyTone = '#fb923c';
    urgencyBand = 'degraded';
  }

  return (
    <article
      style={{
        padding: 14,
        borderRadius: 14,
        border: `1px solid ${expired ? 'rgba(239,68,68,0.35)' : 'var(--border)'}`,
        background: 'var(--bg-raised)',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <code
          style={{
            fontSize: '0.6875rem',
            background: 'var(--bg-overlay)',
            padding: '2px 6px',
            borderRadius: 4,
            border: '1px solid var(--border)',
          }}
        >
          {row.id.slice(0, 8)}…
        </code>
        <StatusChip band={bandFromStatus(row.status)} size="sm">
          {row.status}
        </StatusChip>
        <StatusChip band={urgencyBand} size="sm" pulse={expired || (daysLeft != null && daysLeft <= 3)}>
          {urgencyLabel}
        </StatusChip>
        <span style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>
          {row.plan_tier}
        </span>
      </header>

      <div>
        <div
          style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: '0.6875rem', color: 'var(--text-muted)',
            marginBottom: 4,
          }}
        >
          <span>Day {Math.min(Math.floor(elapsedMs / 86_400_000), TRIAL_DURATION_DAYS)} of {TRIAL_DURATION_DAYS}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(pct)}%</span>
        </div>
        <div
          style={{
            height: 6,
            borderRadius: 999,
            background: 'var(--bg-overlay)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.min(100, pct)}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${urgencyTone}, ${urgencyTone}aa)`,
              transition: 'width 240ms ease',
            }}
          />
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gap: 6,
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          fontSize: '0.75rem',
        }}
      >
        <KV label="Issued">{row.issued_at.slice(0, 10)}</KV>
        <KV label="Expires">{row.expires_at?.slice(0, 10) ?? '—'}</KV>
        <KV label="Activations">
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {row.activation_count} / {row.max_activations}
          </span>
        </KV>
        <KV label="License id">
          <code style={{ fontSize: '0.6875rem' }}>{row.id.slice(0, 12)}…</code>
        </KV>
      </div>

      <Button
        variant={expired ? 'outline' : 'default'}
        size="sm"
        onClick={onConvert}
        style={{ alignSelf: 'flex-start' }}
      >
        {expired ? <AlertOctagon width={14} height={14} /> : <CheckCircle2 width={14} height={14} />}
        {expired ? 'Review (expired)' : 'Convert to paid'}
        <ArrowRight width={12} height={12} />
      </Button>
    </article>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)' }}>{children}</span>
    </div>
  );
}

